
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { ChefHat, Users, DollarSign, Clock, TrendingUp, Utensils } from 'lucide-react';

const RestaurantPEO = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-amber-50">
      <Helmet>
        <title>PEO Services for Restaurants & Food Service | The PEO App</title>
        <meta name="description" content="Specialized PEO services for restaurants, food service, and hospitality businesses. Expert tip reporting, high turnover management, and restaurant-specific HR solutions." />
        <link rel="canonical" href="https://thepeoapp.com/industries/restaurant" />
        <meta name="keywords" content="restaurant PEO, food service PEO, restaurant HR services, tip reporting, restaurant payroll, hospitality PEO, restaurant workers comp" />
        
        <meta property="og:title" content="PEO Services for Restaurants & Food Service" />
        <meta property="og:description" content="Specialized PEO solutions for restaurants with tip reporting and high turnover management expertise." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/industries/restaurant" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <ChefHat className="h-16 w-16 text-amber-600 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              PEO Services for Restaurants & Food Service
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Specialized Professional Employer Organization services for restaurants, food service establishments, 
              and hospitality businesses. Expert tip reporting, turnover management, and food service HR solutions.
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg mb-16">
            <h2 className="text-3xl font-bold mb-6">Restaurant Industry Challenges</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Users className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">High Employee Turnover</h3>
                    <p className="text-gray-700">Restaurant industry averages 75% annual turnover rate</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <DollarSign className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Tip Reporting Compliance</h3>
                    <p className="text-gray-700">Complex tip allocation, reporting, and tax compliance requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Clock className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Irregular Scheduling</h3>
                    <p className="text-gray-700">Variable hours, split shifts, and seasonal workforce fluctuations</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <TrendingUp className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Seasonal Workforce</h3>
                    <p className="text-gray-700">Managing staff levels during busy and slow periods</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Utensils className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Food Safety Training</h3>
                    <p className="text-gray-700">Ongoing food safety certification and training requirements</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <ChefHat className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Multi-Location Management</h3>
                    <p className="text-gray-700">Consistent HR practices across multiple restaurant locations</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Restaurant-Specialized PEO Solutions</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-amber-50 p-6 rounded-xl">
                <DollarSign className="h-12 w-12 text-amber-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Tip Management</h3>
                <p className="text-gray-700">
                  Automated tip reporting, allocation, and tax compliance 
                  to ensure accuracy and reduce administrative burden.
                </p>
              </div>
              
              <div className="bg-amber-50 p-6 rounded-xl">
                <Users className="h-12 w-12 text-amber-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Turnover Reduction</h3>
                <p className="text-gray-700">
                  Proven strategies and benefits programs designed to improve 
                  employee retention in high-turnover environments.
                </p>
              </div>
              
              <div className="bg-amber-50 p-6 rounded-xl">
                <Clock className="h-12 w-12 text-amber-600 mb-4" />
                <h3 className="text-xl font-semibold mb-3">Flexible Scheduling</h3>
                <p className="text-gray-700">
                  Advanced scheduling tools and labor law compliance 
                  for variable schedules and seasonal fluctuations.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-6">Find Restaurant-Specialized PEOs</h2>
            <p className="text-lg text-gray-700 mb-8">
              Connect with PEO providers who understand the unique challenges of food service 
              and have proven experience managing restaurant workforces.
            </p>
            <Link to="/application">
              <Button className="bg-[#00a86b] hover:bg-[#008f5a] text-white text-lg px-8 py-6 rounded-full">
                Get Restaurant PEO Quotes
              </Button>
            </Link>
          </div>

          <div className="bg-gray-50 p-8 rounded-xl">
            <h2 className="text-2xl font-bold mb-6">Food Service Businesses We Serve</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Full-Service Restaurants</h3>
                <p className="text-sm text-gray-600">Casual dining, fine dining, family restaurants</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Fast Food & QSR</h3>
                <p className="text-sm text-gray-600">Quick service, fast casual, drive-thru</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Catering</h3>
                <p className="text-sm text-gray-600">Event catering, corporate catering</p>
              </div>
              <div className="text-center p-4 bg-white rounded-lg">
                <h3 className="font-semibold">Bars & Nightlife</h3>
                <p className="text-sm text-gray-600">Sports bars, nightclubs, breweries</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RestaurantPEO;
